// Fiche d'un ordre de mission : objet contrôlé, période, consignes, carte, puis constat selon la
// grille du formulaire de la version citée (conforme, non conforme, sans objet ; observation exigée
// quand le point l'exige ou n'est pas conforme ; photo exigée sauf sans objet : règle du contrat
// verifierGrilleConstat, D-122), observations, position relevée, conclusion déduite, mesure
// corrective proposée quand un point n'est pas conforme (D-133). Enregistré sur l'appareil (opération
// constat du contrat), synchronisé ensuite par la même file que les rapports (D-045).
import { conclusionConstat, REPONSES_POINT, type ReponsePoint as ValeurPointControle } from '@cc/contrats';
import { Bouton, Champ, EtatVide, Formulaire, LienBouton, Squelette, ZoneTexte, type ErreurChamp } from '@cc/ui';
import { LocateFixed } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { chargeConstat, problemesGrille } from '../../terrain/charges';
import { LIBELLES_ETAT_ORDRE, LIBELLES_OBJET_ORDRE, dateHeure, dateLongue, etatLocalObjet, etatLocalOperation } from '../../terrain/etat-local';
import { uuidV7 } from '../../terrain/identifiants';
import { releverPosition, type EtatPosition } from '../../terrain/position';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { useOperationsLocales, useOrdreLocal } from '../../terrain/useTerrain';
import { CarteTerrain } from './CarteTerrain';
import { Bloc, Definitions, EnTeteTerrain, EtatLocal, Ligne, Liste, Section, TexteEtat } from './commun';
import { SaisiePhotos, versPhotosAEnregistrer, type PhotoSaisie } from './SaisiePhotos';
import styles from './terrain.module.css';

interface ReponseSaisie {
  valeur: ValeurPointControle | '';
  observation: string;
  photos: PhotoSaisie[];
}

const LIBELLES_VALEUR: Record<ValeurPointControle, string> = REPONSES_POINT;
const LIBELLE_CONCLUSION: Record<ValeurPointControle, string> = { conforme: 'conforme', non_conforme: 'non conforme', sans_objet: 'sans objet' };

export function PageOrdreMission() {
  const { id } = useParams();
  const ordre = useOrdreLocal(id);
  const operations = useOperationsLocales(id);
  const [reponses, setReponses] = useState<Record<string, ReponseSaisie>>({});
  const [observations, setObservations] = useState('');
  const [mesure, setMesure] = useState({ description: '', delai: '15' });
  const [photos, setPhotos] = useState<PhotoSaisie[]>([]);
  const [position, setPosition] = useState<EtatPosition | null>(null);
  const [releve, setReleve] = useState(false);
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (ordre.chargement) return <Squelette lignes={6} libelle="Lecture de l'ordre de mission sur l'appareil" />;
  const o = ordre.donnees;
  if (!o) {
    return (
      <>
        <EnTeteTerrain titre="Ordre de mission introuvable" retour="/terrain" />
        <EtatVide titre="Cet ordre de mission n'est pas enregistré sur cet appareil" niveau={2} action={<LienBouton href="/terrain">Retour à mon travail</LienBouton>} />
      </>
    );
  }
  const ops = operations.donnees ?? [];
  const constats = ops.filter((x) => x.type === 'constat');
  const points = o.formulaire.points;
  const reponse = (code: string): ReponseSaisie => reponses[code] ?? { valeur: '', observation: '', photos: [] };
  const changer = (code: string, partiel: Partial<ReponseSaisie>) => setReponses((r) => ({ ...r, [code]: { ...reponse(code), ...partiel } }));
  const nonConformes = points.filter((p) => reponse(p.code).valeur === 'non_conforme').length;
  const conclusion = conclusionConstat(points.filter((p) => reponse(p.code).valeur).map((p) => ({ reponse: reponse(p.code).valeur as ValeurPointControle })));
  const pourCharge = () => Object.fromEntries(points.map((p) => [p.code, { ...reponse(p.code), photos: reponse(p.code).photos.map((ph) => ph.id_client) }]));

  const relever = async () => {
    setReleve(true);
    setPosition(await releverPosition(10_000));
    setReleve(false);
  };

  const enregistrer = async () => {
    const fautes: ErreurChamp[] = [];
    for (const p of points) if (!reponse(p.code).valeur) fautes.push({ champ: `point-${p.code}`, message: `Répondez au point « ${p.libelle} »` });
    // Règle du contrat (verifierGrilleConstat) : observation et photo selon le point et la réponse.
    for (const pb of problemesGrille(o, pourCharge())) {
      if (!pb.code) continue;
      fautes.push({ champ: pb.message.startsWith('Photo') ? `photos-${pb.code}` : `observation-${pb.code}`, message: pb.message });
    }
    const delai = Number(mesure.delai);
    if (conclusion === 'non_conforme') {
      if (mesure.description.trim().length < 3) fautes.push({ champ: 'mesure-description', message: 'Proposez une mesure corrective pour les points non conformes' });
      if (!Number.isInteger(delai) || delai < 1 || delai > 365) fautes.push({ champ: 'mesure-delai', message: 'Le délai est un nombre de jours entre 1 et 365' });
    }
    setErreurs(fautes);
    if (fautes.length) return;
    setEnCours(true);
    setMessage(null);
    try {
      const charge = chargeConstat(o, pourCharge(), { observations, position: position?.position ?? null, mesure: conclusion === 'non_conforme' ? { description: mesure.description, delai_jours: delai } : null, effectue_le: new Date().toISOString() });
      const toutes = [...points.flatMap((p) => reponse(p.code).photos), ...photos];
      await synchroniseurPartage().enfiler(
        { id_client: uuidV7(), type: 'constat', objet_type: 'ordre_mission', objet_id: o.id, version_attendue: o.version, enregistre_le: new Date().toISOString(), charge },
        { titre: `Constat (${LIBELLE_CONCLUSION[conclusion]})`, objet_libelle: `${o.numero} · ${o.objet_libelle}`, photos: versPhotosAEnregistrer(toutes, 'constat') },
      );
      setMessage(`Constat ${LIBELLE_CONCLUSION[conclusion]} enregistré sur cet appareil avec ${toutes.length} photo(s)${position?.position ? ' et votre position' : ''}.`);
      setReponses({});
      setObservations('');
      setPhotos([]);
    } catch (e) {
      setMessage(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const etatLocal = etatLocalObjet(ops, o.id);
  return (
    <>
      <EnTeteTerrain
        titre={o.objet_libelle}
        surTitre={o.numero}
        retour="/terrain"
        libelleRetour="Mon travail"
        sousTitre={
          <>
            {LIBELLES_OBJET_ORDRE[o.objet_type] ?? o.objet_type} · {LIBELLES_ETAT_ORDRE[o.etat]}
            {etatLocal ? (
              <>
                {' · '}
                <EtatLocal etat={etatLocal} />
              </>
            ) : null}
          </>
        }
      />
      <Bloc etiquette="Consignes">
        <p className={styles.texte}>{o.consignes ?? 'Aucune consigne particulière.'}</p>
      </Bloc>
      <Definitions elements={[{ terme: 'Lieu', valeur: o.lieu ?? 'Non renseigné' }, { terme: 'Période de contrôle', valeur: `Du ${dateLongue(o.periode_debut)} au ${dateLongue(o.periode_fin)}` }, { terme: 'Formulaire', valeur: `${o.formulaire.nom} (version ${o.formulaire.version})` }]} />
      {o.position ? <CarteTerrain titre="Carte du contrôle" cible={{ libelle: o.objet_libelle, position: o.position }} suivre hauteur={260} /> : null}
      {constats.length ? (
        <Section titre="Constats enregistrés">
          <Liste libelle="Constats">
            {constats.map((c) => {
              const e = etatLocalOperation(c);
              return <Ligne key={c.id_client} href={e === 'conflit' ? '/terrain/conflits' : undefined} titre={c.titre} sousTitre={`Enregistré le ${dateHeure(c.enregistre_le)}`} droite={e ? <EtatLocal etat={e} /> : <TexteEtat libelle="Rejeté" ton="attention" />} />;
            })}
          </Liste>
        </Section>
      ) : null}
      <Section titre="Constat">
        <Formulaire
          libelle="Formulaire de constat"
          onSoumettre={() => void enregistrer()}
          erreurs={erreurs}
          actions={
            <Bouton type="submit" chargement={enCours} libelleChargement="Enregistrement sur l'appareil en cours">
              Enregistrer le constat
            </Bouton>
          }
        >
          {message ? (
            <p role="status" className={styles.aide}>
              {message}
            </p>
          ) : null}
          {points.map((p, i) => {
            const r = reponse(p.code);
            const erreurPoint = erreurs.find((f) => f.champ === `point-${p.code}`)?.message;
            const erreurPhoto = erreurs.find((f) => f.champ === `photos-${p.code}`)?.message;
            const erreurObservation = erreurs.find((f) => f.champ === `observation-${p.code}`)?.message;
            return (
              <fieldset key={p.code} className={styles.pointControle} id={`point-${p.code}`}>
                <legend>
                  <span className={styles.numero}>{String(i + 1).padStart(2, '0')}</span> {p.libelle}
                  {p.photo_exigee ? <span className={styles.aide}> (photo exigée)</span> : null}
                </legend>
                {p.aide ? <p className={styles.aide}>{p.aide}</p> : null}
                <div className={styles.choix} role="radiogroup" aria-label={p.libelle}>
                  {(['conforme', 'non_conforme', 'sans_objet'] as const).map((v) => (
                    <label key={v}>
                      <input type="radio" name={`point-${p.code}`} value={v} checked={r.valeur === v} onChange={() => changer(p.code, { valeur: v })} />
                      {LIBELLES_VALEUR[v]}
                    </label>
                  ))}
                </div>
                {erreurPoint ? (
                  <p className={styles.aide} role="alert">
                    {erreurPoint}
                  </p>
                ) : null}
                {p.observation !== 'aucune' || r.valeur === 'non_conforme' ? (
                  <Champ
                    id={`observation-${p.code}`}
                    libelle="Observation"
                    value={r.observation}
                    onChange={(e) => changer(p.code, { observation: e.target.value })}
                    erreur={erreurObservation}
                    aide={r.valeur === 'non_conforme' ? 'Exigée pour un point non conforme.' : undefined}
                    {...(p.observation === 'obligatoire' || r.valeur === 'non_conforme' ? { required: true } : { facultatif: true })}
                  />
                ) : null}
                {p.photo_exigee || r.valeur === 'non_conforme' ? <SaisiePhotos id={`photos-${p.code}`} libelle={`Photos du point ${i + 1}`} photos={r.photos} onChange={(ph) => changer(p.code, { photos: ph })} max={3} /> : null}
                {erreurPhoto ? (
                  <p className={styles.aide} role="alert">
                    {erreurPhoto}
                  </p>
                ) : null}
              </fieldset>
            );
          })}
          <ZoneTexte id="constat-observations" libelle="Observations générales" rows={3} value={observations} onChange={(e) => setObservations(e.target.value)} facultatif />
          <div className={styles.ligneBoutons}>
            <Bouton variante="secondaire" icone={LocateFixed} onClick={() => void relever()} chargement={releve} libelleChargement="Relevé de la position en cours">
              Relever ma position
            </Bouton>
            <span className={styles.aide}>{position?.position ? `Position relevée (précision ${position.position.precision_m ?? '?'} m)` : position?.erreur ? position.erreur : 'Aucune position relevée'}</span>
          </div>
          <SaisiePhotos id="constat-photos" libelle="Photos d'ensemble" photos={photos} onChange={setPhotos} max={4} />
          <Bloc etiquette="Conclusion" titre={conclusion === 'non_conforme' ? `Non conforme : ${nonConformes} point(s)` : conclusion === 'sans_objet' ? 'Sans objet' : 'Conforme'}>
            {conclusion === 'non_conforme' ? (
              <>
                <ZoneTexte id="mesure-description" libelle="Mesure corrective proposée" rows={2} value={mesure.description} onChange={(e) => setMesure((m) => ({ ...m, description: e.target.value }))} erreur={erreurs.find((f) => f.champ === 'mesure-description')?.message} required />
                <Champ id="mesure-delai" libelle="Délai proposé (jours)" inputMode="numeric" value={mesure.delai} onChange={(e) => setMesure((m) => ({ ...m, delai: e.target.value }))} erreur={erreurs.find((f) => f.champ === 'mesure-delai')?.message} required />
                <p className={styles.aide}>Aucune sanction n&apos;est prononcée ici : la mesure est proposée au responsable, qui décide de la transmission.</p>
              </>
            ) : (
              <p className={styles.aide}>Tous les points répondus sont conformes ou sans objet.</p>
            )}
          </Bloc>
        </Formulaire>
      </Section>
    </>
  );
}
